/** The two terminal background appearances a theme targets. */
export type ThemeAppearance = 'dark' | 'light';

/** Names of the built-in themes. */
export type ThemeName = 'dark' | 'light' | 'dark-high-contrast' | 'light-high-contrast';

/**
 * The user-facing theme preference: a concrete built-in theme, or `auto` to
 * follow the detected terminal appearance.
 */
export type ThemeSetting = 'auto' | ThemeName;

export const THEME_SETTINGS = [
  'auto',
  'dark',
  'light',
  'dark-high-contrast',
  'light-high-contrast',
] as const satisfies readonly ThemeSetting[];

export const isThemeSetting = (value: unknown): value is ThemeSetting =>
  (THEME_SETTINGS as readonly unknown[]).includes(value);

/** Background colors for the diff renderer, one pair per diff line type. */
export interface DiffBackgroundTokens {
  addPrefix: string;
  add: string;
  delPrefix: string;
  del: string;
  plainPrefix: string;
  plain: string;
  hunkPrefix: string;
  hunk: string;
}

/**
 * Semantic color roles used by TUI components, grouped by facet
 * (`text`/`background`) following the naming direction of the Pajamas
 * design-token directory
 * (https://design.gitlab.com/product-foundations/design-tokens-directory),
 * so a role can later grow other facets (e.g. a `border` group) without
 * renaming. Components reference roles (e.g. `text.danger`) rather than
 * concrete colors so that a theme can restyle the whole interface. Values are
 * Ink-compatible colors: ANSI color names inherit the user's terminal
 * palette, while hex values render as-is (chalk downgrades them on terminals
 * without truecolor support).
 */
export interface ThemeTokens {
  text: {
    /** Emphasized foreground, e.g. selection cursors. */
    default: string;
    subtle: string;
    /** Foreground rendered on top of status-colored backgrounds. */
    inverse: string;
    success: string;
    danger: string;
    warning: string;
    info: string;
    /** Titles, borders, selection highlights. */
    accent: string;
    link: string;
    /** Inline code and code fences in markdown. */
    code: string;
    agent: {
      build: string;
      plan: string;
    };
  };
  background: {
    userMessage: string;
    diff: DiffBackgroundTokens;
  };
}

/** A resolved theme, as returned by `useTheme()`. */
export interface Theme {
  name: ThemeName;
  appearance: ThemeAppearance;
  tokens: ThemeTokens;
}
